import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PhotoItem, PhotoSession, Treatment } from '@/lib/types';
import {
  Banner,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Tag,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { dt } from '@/lib/format';

const ANGLES = [
  { id: 'front', label: '正面' },
  { id: 'left', label: '左 45°' },
  { id: 'right', label: '右 45°' },
  { id: 'close', label: '局部特寫' },
] as const;

const SCOPES = [
  { id: 'clinical', label: '醫療紀錄' },
  { id: 'before_after', label: 'Before/After 比對' },
  { id: 'marketing_anonymous', label: '匿名行銷' },
  { id: 'data_pool', label: '大數據案例庫' },
] as const;

function PhotoTile({ photo }: { photo: PhotoItem }) {
  const isBefore = photo.phase === 'before';
  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          aspectRatio: '3/4',
          borderRadius: 6,
          border: '1px solid var(--line)',
          overflow: 'hidden',
          background: isBefore ? 'linear-gradient(135deg,#E8E0D8,#D8C8B8)' : 'linear-gradient(135deg,#F5DCC8,#E8C7B8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {photo.url ? (
          <img src={`/api/v1/compliance/photos/${photo.id}/file`} alt={photo.angle} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <svg viewBox="0 0 100 130" style={{ width: '55%', opacity: 0.4 }} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="1.5">
            <ellipse cx="50" cy="60" rx="30" ry="42" />
            <circle cx="40" cy="55" r="2" fill="rgba(0,0,0,0.3)" />
            <circle cx="60" cy="55" r="2" fill="rgba(0,0,0,0.3)" />
            <path d="M42 78 Q50 84 58 78" />
          </svg>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          top: 4,
          left: 4,
          fontSize: 9,
          padding: '1px 5px',
          borderRadius: 3,
          background: isBefore ? 'var(--ink-3)' : 'var(--success)',
          color: '#fff',
          fontWeight: 700,
        }}
      >
        {isBefore ? 'Before' : 'After'}
      </div>
      <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600 }}>{ANGLES.find((a) => a.id === photo.angle)?.label}</div>
    </div>
  );
}

export function PhotosPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [customerId, setCustomerId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [newSession, setNewSession] = useState({ treatmentId: '', scopes: ['clinical', 'before_after'] as string[] });
  const [photoOpen, setPhotoOpen] = useState(false);
  const [newPhoto, setNewPhoto] = useState<{ angle: string; phase: string; file: File | null; previewUrl: string; fileName: string }>({ angle: 'front', phase: 'before', file: null, previewUrl: '', fileName: '' });

  const onPickFile = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      fail(new Error('請選擇圖片檔（jpg / png …）'));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      fail(new Error('圖片需小於 8MB'));
      return;
    }
    setNewPhoto((s) => ({ ...s, file, previewUrl: URL.createObjectURL(file), fileName: file.name }));
  };

  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const sessions = useQuery({
    queryKey: ['photo-sessions', customerId],
    enabled: !!customerId,
    queryFn: () => api.get<PhotoSession[]>(`/compliance/photo-sessions?customerId=${customerId}`),
  });
  const photos = useQuery({
    queryKey: ['photos', sessionId],
    enabled: !!sessionId,
    queryFn: () => api.get<PhotoItem[]>(`/compliance/photo-sessions/${sessionId}/photos`),
  });
  const verify = useQuery({
    queryKey: ['verify', sessionId],
    enabled: !!sessionId,
    queryFn: () => api.get<{ valid: boolean; count: number }>(`/compliance/photo-sessions/${sessionId}/verify`),
  });

  const createSession = useMutation({
    mutationFn: () =>
      api.post<PhotoSession>('/compliance/photo-sessions', {
        customerId,
        treatmentId: newSession.treatmentId,
        consentScopes: newSession.scopes,
      }),
    onSuccess: (s) => {
      ok('影像組已建立');
      setSessionOpen(false);
      setNewSession({ treatmentId: '', scopes: ['clinical', 'before_after'] });
      setSessionId(s.id);
      void qc.invalidateQueries({ queryKey: ['photo-sessions', customerId] });
    },
    onError: fail,
  });
  const addPhoto = useMutation({
    mutationFn: () => {
      if (!newPhoto.file) throw new Error('請先選擇照片');
      // 欄位須排在檔案之前（multipart 串流解析）
      const form = new FormData();
      form.append('sessionId', sessionId);
      form.append('angle', newPhoto.angle);
      form.append('phase', newPhoto.phase);
      form.append('file', newPhoto.file, newPhoto.file.name);
      return api.upload('/compliance/photos/upload', form);
    },
    onSuccess: () => {
      ok('影像已上傳並加入存證鏈');
      setPhotoOpen(false);
      setNewPhoto({ angle: 'front', phase: 'before', file: null, previewUrl: '', fileName: '' });
      void qc.invalidateQueries({ queryKey: ['photos', sessionId] });
      void qc.invalidateQueries({ queryKey: ['verify', sessionId] });
    },
    onError: fail,
  });

  const sessionList = sessions.data ?? [];
  const photoList = photos.data ?? [];
  const tName = (id: string) => treatments.data?.find((t) => t.id === id)?.name ?? id;
  const toggleScope = (id: string) =>
    setNewSession((s) => ({
      ...s,
      scopes: s.scopes.includes(id) ? s.scopes.filter((x) => x !== id) : [...s.scopes, id],
    }));

  return (
    <div>
      <PageHeader title="顧客影像庫" subtitle="同意書追蹤 · Before/After 影像組 · SHA-256 存證鏈防竄改" />
      <Banner msg={msg} />

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16 }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--line-soft)' }}>
            <Field label="選擇顧客">
              <CustomerPicker
                value={customerId}
                onChange={(id) => {
                  setCustomerId(id);
                  setSessionId('');
                }}
              />
            </Field>
          </div>
          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            {sessionList.map((s) => (
              <button
                key={s.id}
                onClick={() => setSessionId(s.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 0,
                  borderBottom: '1px solid var(--line-soft)',
                  borderLeft: sessionId === s.id ? '3px solid var(--primary)' : '3px solid transparent',
                  background: sessionId === s.id ? 'var(--primary-tint)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{tName(s.treatmentId)}</div>
                <div style={{ fontSize: 10.5, color: 'var(--ink-4)', marginTop: 2 }} className="num">
                  {dt(s.createdAt)}
                </div>
                <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>
                  {s.consentScopes.map((sc) => (
                    <Tag key={sc} tone="tag">
                      {SCOPES.find((x) => x.id === sc)?.label ?? sc}
                    </Tag>
                  ))}
                </div>
              </button>
            ))}
            {customerId && sessionList.length === 0 && !sessions.isLoading && (
              <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12 }}>此顧客尚無影像組</div>
            )}
            {!customerId && <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12 }}>請先選擇顧客</div>}
          </div>
          {customerId && (
            <div style={{ padding: 10, borderTop: '1px solid var(--line-soft)' }}>
              <button className="btn btn-primary" style={{ width: '100%', fontSize: 11.5 }} onClick={() => setSessionOpen(true)}>
                <Icon d={I.plus} size={12} /> 建立影像組
              </button>
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 16, minHeight: 460 }}>
          {sessionId ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>影像組</h3>
                {verify.data && (
                  <Tag tone={verify.data.valid ? 'success' : 'danger'} dot>
                    存證鏈{verify.data.valid ? '完整' : '異常'} · {verify.data.count} 張
                  </Tag>
                )}
                <div style={{ flex: 1 }} />
                <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setPhotoOpen(true)}>
                  <Icon d={I.plus} size={12} /> 上傳影像
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {photoList.map((p) => (
                  <PhotoTile key={p.id} photo={p} />
                ))}
              </div>
              {photoList.length === 0 && !photos.isLoading && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 12, border: '2px dashed var(--line)', borderRadius: 8 }}>
                  尚無影像，點「上傳影像」加入存證鏈
                </div>
              )}
            </>
          ) : (
            <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
              選擇左側影像組
            </div>
          )}
        </div>
      </div>

      <Modal open={sessionOpen} onClose={() => setSessionOpen(false)} width={480}>
        <ModalHeader title="建立影像組" sub="綁定療程並勾選影像同意範圍" onClose={() => setSessionOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="療程 *">
            <Select
              value={newSession.treatmentId}
              onChange={(val) => setNewSession((s) => ({ ...s, treatmentId: val }))}
              placeholder="— 選擇療程 —"
              options={(treatments.data ?? []).map((t) => ({ value: t.id, label: `${t.name}` }))}
            />
          </Field>
          <Field label="影像同意範圍 *">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SCOPES.map((sc) => (
                <label key={sc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-2)' }}>
                  <input type="checkbox" checked={newSession.scopes.includes(sc.id)} onChange={() => toggleScope(sc.id)} />
                  {sc.label}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setSessionOpen(false)}>
            取消
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1.5 }}
            disabled={!newSession.treatmentId || newSession.scopes.length === 0 || createSession.isPending}
            onClick={() => createSession.mutate()}
          >
            建立影像組
          </button>
        </div>
      </Modal>

      <Modal open={photoOpen} onClose={() => setPhotoOpen(false)} width={420}>
        <ModalHeader title="上傳影像" sub="加入 SHA-256 存證鏈" onClose={() => setPhotoOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="角度">
              <Select
                value={newPhoto.angle}
                onChange={(val) => setNewPhoto((s) => ({ ...s, angle: val }))}
                options={ANGLES.map((a) => ({ value: a.id, label: a.label }))}
              />
            </Field>
            <Field label="階段">
              <Select
                value={newPhoto.phase}
                onChange={(val) => setNewPhoto((s) => ({ ...s, phase: val }))}
                options={[
                  { value: 'before', label: 'Before · 術前' },
                  { value: 'after', label: 'After · 術後' },
                ]}
              />
            </Field>
          </div>
          <Field label="選擇照片檔 *">
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: newPhoto.previewUrl ? 8 : 24,
                border: '2px dashed var(--line)',
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--ink-3)',
                fontSize: 12,
              }}
            >
              {newPhoto.previewUrl ? (
                <img src={newPhoto.previewUrl} alt="預覽" style={{ maxHeight: 160, maxWidth: '100%', borderRadius: 6 }} />
              ) : (
                <>
                  <Icon d={I.eye} size={20} />
                  <span>點此選擇照片（jpg / png，&lt; 4MB）</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
            </label>
            {newPhoto.fileName && (
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>已選：{newPhoto.fileName}（重選請再點上方）</div>
            )}
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setPhotoOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!newPhoto.file || addPhoto.isPending} onClick={() => addPhoto.mutate()}>
            <Icon d={I.check} size={13} /> 加入存證鏈
          </button>
        </div>
      </Modal>
    </div>
  );
}
